import { db } from "../../../firebase/firebase";
import {
  collection,
  doc,
  getDocs,
  serverTimestamp,
  setDoc,
  writeBatch,
} from "firebase/firestore";

const companyKey = (company) =>
  String(company || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const safeId = (value) =>
  String(value || "")
    .trim()
    .replace(/[^a-zA-Z0-9_-]+/g, "_")
    .slice(0, 180);

export async function saveDailyRideHailingEarnings({
  rows,
  platform,
  company,
  reportDate,
  mappings,
  sourceFileName,
  uploadedBy = "",
}) {
  if (!reportDate) throw new Error("Report date is required.");
  if (!company) throw new Error("Company is required.");
  if (!platform) throw new Error("Platform is required.");

  const p = String(platform).toLowerCase();
  if (!['uber', 'bolt'].includes(p)) {
    throw new Error("Only Uber and Bolt are supported for daily uploads.");
  }

  const cKey = companyKey(company);
  const uploadKey = `${reportDate}__${cKey}__${p}`;
  const recordsRef = collection(db, "dailyRideHailingEarnings", reportDate, "records");

  // Re-upload is treated as a replacement for the same company/platform/date.
  // This prevents duplicate daily income without touching weekly Car Sheet data.
  const existingSnap = await getDocs(recordsRef);
  const oldDocs = existingSnap.docs.filter((d) => {
    const data = d.data() || {};
    return data.companyKey === cKey && String(data.platform || "").toLowerCase() === p;
  });

  const batch = writeBatch(db);
  oldDocs.forEach((old) => batch.delete(old.ref));
  let savedCount = 0;
  let bankTotal = 0;
  let cashTotal = 0;

  rows.forEach((row, index) => {
    const internalDriverId = mappings[row.driver_id];
    if (!internalDriverId) return;

    const bankIncome = Number(row.bank_income || 0);
    const cashIncome = Number(row.cash_income || 0);
    const recordId = safeId(`${cKey}__${p}__${internalDriverId}__${index}`);

    batch.set(doc(recordsRef, recordId), {
      reportDate,
      platform: p,
      company,
      incomeCompany: company,
      companyKey: cKey,
      driverId: internalDriverId,
      // Keep the platform name only as source/audit data. Reports resolve the
      // display name from drivers/{driverId}.fullName.
      driverName: row.name || "",
      sourceDriverName: row.name || "",
      sourceDriverId: row.driver_id || "",
      bankIncome,
      cashIncome,
      sourceFileName: sourceFileName || "",
      uploadKey,
      updatedAt: serverTimestamp(),
    });

    savedCount += 1;
    bankTotal += bankIncome;
    cashTotal += cashIncome;
  });

  await batch.commit();

  await setDoc(
    doc(db, "dailyRideHailingUploads", uploadKey),
    {
      uploadKey,
      reportDate,
      platform: p,
      company,
      incomeCompany: company,
      companyKey: cKey,
      sourceFileName: sourceFileName || "",
      rowCount: savedCount,
      bankTotal,
      cashTotal,
      uploadedBy: uploadedBy || "",
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );

  return { savedCount, bankTotal, cashTotal, uploadKey, replacedCount: oldDocs.length };
}
